import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import { toast } from "sonner";
import { PageHero, Section, fadeUp } from "../components/sections";
import SeoHead from "../components/SeoHead";
import { apiClient } from "../lib/site";
import { getUtm } from "../lib/utm";

export default function CareersPage() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeRole, setActiveRole] = useState(null);

  useEffect(() => {
    apiClient
      .get("/careers")
      .then((r) => setRoles(r.data))
      .catch(() => setRoles([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="careers-page">
      <SeoHead
        title="Careers"
        description="A UK-headquartered, remote-friendly software consultancy hiring senior engineers, AI/ML, cloud architects, designers, engagement managers and QA leads. We hire infrequently and carefully."
        path="/careers"
      />
      <PageHero
        overline="Careers"
        title={<>Built for engineers, designers and strategists who want to ship things that matter.</>}
        intro="We are a UK-headquartered, remote-friendly firm with practitioners across the UK, Europe and the Americas. We hire infrequently and carefully. The bar is high; the work is real."
        testId="careers-hero"
      />

      <Section className="bg-[#050505]">
        <p className="overline mb-6">Open Roles</p>
        <h2
          className="font-serif-display font-light text-white leading-[1.05] tracking-tight mb-16"
          style={{ fontSize: "clamp(32px, 5vw, 56px)", letterSpacing: "-0.025em" }}
        >
          Six positions across engineering, design, and delivery.
        </h2>
        {loading && <p className="text-zinc-500 text-sm">Loading…</p>}
        <div className="space-y-px bg-[#27272A]">
          {roles.map((r, i) => (
            <motion.button
              key={r.id}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.04 }}
              onClick={() => setActiveRole(r)}
              className="w-full bg-[#050505] hover:bg-[#0A0A0C] transition-colors p-8 md:p-10 text-left group"
              data-testid={`career-row-${r.id}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
                <div className="md:col-span-6">
                  <h3 className="font-serif-display text-2xl md:text-3xl text-white font-light">
                    {r.title}
                  </h3>
                </div>
                <div className="md:col-span-2">
                  <p className="text-zinc-400 text-sm">{r.department}</p>
                </div>
                <div className="md:col-span-3">
                  <p className="text-zinc-500 text-sm">{r.location}</p>
                </div>
                <div className="md:col-span-1 flex md:justify-end">
                  <span className="inline-flex items-center gap-2 text-white text-sm tracking-wide">
                    <ArrowRight size={16} strokeWidth={1.5} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </Section>

      <AnimatePresence>
        {activeRole && (
          <RoleDrawer role={activeRole} onClose={() => setActiveRole(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}

function RoleDrawer({ role, onClose }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    portfolio_url: "",
    cover_letter: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.cover_letter) {
      toast.error("Please fill name, email, and cover letter.");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post("/careers/apply", {
        ...form,
        role_id: role.id,
        role_title: role.title,
        utm: getUtm(),
      });
      toast.success("Application received. We'll be in touch.");
      onClose();
    } catch (err) {
      toast.error("Could not submit application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="role-drawer"
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-stretch justify-end"
      onClick={onClose}
    >
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
        className="w-full md:w-[640px] bg-[#0A0A0C] border-l border-white/10 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-[#0A0A0C] border-b border-white/10 px-8 py-5 flex items-center justify-between">
          <p className="overline">Position Detail</p>
          <button
            onClick={onClose}
            className="p-2 text-zinc-400 hover:text-white transition"
            aria-label="Close"
            data-testid="role-drawer-close"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>
        <div className="px-8 py-10">
          <h3 className="font-serif-display text-4xl md:text-5xl text-white font-light leading-tight">
            {role.title}
          </h3>
          <p className="mt-4 text-zinc-400 text-sm">
            {role.department} · {role.location} · {role.type}
          </p>
          <p className="mt-8 text-zinc-300 text-base leading-relaxed">{role.description}</p>

          <p className="overline mt-12 mb-4">What we're looking for</p>
          <ul className="space-y-2 text-zinc-300 text-sm leading-relaxed">
            {role.requirements.map((req, i) => (
              <li key={i} className="flex gap-3">
                <span style={{ color: "#C2533B" }}>—</span>
                <span>{req}</span>
              </li>
            ))}
          </ul>

          <form
            onSubmit={submit}
            className="mt-16 space-y-6 border-t border-white/10 pt-10"
            data-testid="role-apply-form"
          >
            <p className="overline">Apply</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <input
                className="input-line"
                placeholder="Full name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="apply-name"
                required
              />
              <input
                type="email"
                className="input-line"
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="apply-email"
                required
              />
            </div>
            <input
              className="input-line"
              placeholder="Portfolio / LinkedIn URL (optional)"
              value={form.portfolio_url}
              onChange={(e) => setForm({ ...form, portfolio_url: e.target.value })}
              data-testid="apply-portfolio"
            />
            <textarea
              className="input-line resize-none"
              placeholder="Why this role? Tell us about a system you shipped recently."
              rows={5}
              value={form.cover_letter}
              onChange={(e) => setForm({ ...form, cover_letter: e.target.value })}
              data-testid="apply-cover-letter"
              required
            />
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary disabled:opacity-50"
              data-testid="apply-submit"
            >
              {submitting ? "Submitting…" : "Send Application"}
            </button>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
}
